import { MouseEvent, useState } from "react";
import { styled } from "@mui/material";
import { Menu, MenuItem, Button } from "@kaltura/ds-react-components";
import { Add24Icon } from "@kaltura/ds-react-icons";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { useMediaQuery, translate } from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { CreationToolButtonInjectedProps } from "./CreationToolButtonInjectedProps";
import { CreationToolInjectedProps } from "./CreationToolProps";

const StyledButtons = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
}));

const StyledIcon = styled("img")(({ theme }) => ({
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
    verticalAlign: "middle",
}));

interface CreationToolsButtonsProps {
    creationToolButtons: KmsTypeComponent<any, CreationToolButtonInjectedProps>[];
    onClick: (creationTool: KmsTypeComponent<any, CreationToolInjectedProps>, mediaContext?: string) => void;
    // The context in which media is playing, used when getting player KS
    mediaContext?: string;
}

/**
 * creation tools buttons for search and select
 */
export const CreationToolsButtons = ({ creationToolButtons, onClick, mediaContext }: CreationToolsButtonsProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = !!anchorEl;
    const close = () => setAnchorEl(null);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const buttonId = "searchAndSelect-create-button";
    const menuId = "searchAndSelect-create-menu";

    const onMenuButtonClick = (ev: MouseEvent<HTMLElement>) => {
        // TODO - add analytics
        setAnchorEl(ev.currentTarget);
    };

    return (
        <>
            {!isSmallScreen && (
                <StyledButtons>
                    {creationToolButtons.map((creationToolButton, index) => {
                        const { props } = creationToolButton;
                        const { creationTool } = props;

                        return (
                            <CustomComponent
                                key={index}
                                component={creationToolButton}
                                variant="borderless"
                                color="secondary"
                                onClick={() => onClick(creationTool, mediaContext)}
                            />
                        );
                    })}
                </StyledButtons>
            )}
            {isSmallScreen && (
                <>
                    <Button
                        id={buttonId}
                        onClick={onMenuButtonClick}
                        onKeyDown={(ev) => {
                            if (ev.key === "ArrowDown" || ev.key === " ") {
                                // TODO - add analytics
                                setAnchorEl(ev.currentTarget);
                            }
                        }}
                        aria-haspopup={true}
                        aria-expanded={open}
                        aria-controls={menuId}
                        color="secondary"
                        startIcon={<Add24Icon />}
                    >
                        {translate("Create")}
                    </Button>

                    <Menu
                        id={menuId}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        open={open}
                        onClose={close}
                        slotProps={{
                            list: {
                                "aria-labelledby": buttonId,
                            },
                        }}
                        disablePortal={true}
                    >
                        {creationToolButtons.map((creationToolButton, index) => {
                            const { props } = creationToolButton;
                            const { menuItemProps, label } = props;
                            const { iconUrl, ...rest } = menuItemProps;

                            return (
                                <MenuItem
                                    key={label}
                                    component="a"
                                    {...rest}
                                    onClick={() => {
                                        close();
                                        onClick(props.creationTool, mediaContext);
                                    }}
                                >
                                    <StyledIcon src={iconUrl} />
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </>
            )}
        </>
    );
};
